import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { IRequestService } from '../request.service';
import {
    AlarmsHeatMapReadConfiguration,
    AlarmsTableWidgetReadConfiguration,
    ApprovalTableReadConfiguration,
    Dashboard,
    DashboardPeriod,
    DashboardReadResultDto,
    DashboardValuesReadConfiguration,
    DateOrder,
    DateRange,
    DateValues,
    Dictionary,
    ErrorLevel,
    Group,
    GroupStatesRequest,
    IValueViewModel,
    LogbookEntrySortOrder,
    LogbookTableReadConfiguration,
    LogbookWidgetReadConfiguration,
    QualityWidgetReadConfiguration,
    SpotValuesReadConfiguration,
    TrendLogbookEntriesType,
    WidgetReadConfiguration,
    WidgetValues,
    WidgetValuesReadConfiguration,
    WidgetValuesReadConfigurationDictionary
} from '../../models';
import { IReadSeriesConfiguration, IWidget } from '../../widgets';
import {
    IFrequencyFormatHelperService,
    SeriesDictionary,
    IDashboardService,
    IIdentifierVmService,
    IDateNavigationService,
    IDateRangeService,
    ISeriesService,
    IGroupSelectionService,
    IwidgetIntervalHelperService,
    IDashboardSeriesService,
    IGroupService,
    ITableByGroupService,
    IMapService
} from '../../services';
import { dashboardValuesUrl, guidEmpty, WideError } from '../../common';
import { buildWidgetValues } from './dashboardHelpers';
import { ExtendedDashboardInfo } from './ExtendedDashboardInfo';
import { DashboardInfo } from './DashboardInfo';
import { ApprovalTableConfiguration } from '../../widgets/approvalTable/approvalTable.configuration';
import { alarmsHeatMapWidgetDefinition, spotValuesWidgetDefinition } from '../../widgets/widgets.definition.index';
import { AlarmsTableConfiguration } from '../../widgets/alarmsTable/alarmsTable.configuration';
import { LogbookTableReadMode } from '../../widgets/logbookTable/LogbookTableReadMode';
import { SpotValuesWidgetConfiguration } from '../../widgets/spotValuesWidget/spotValuesWidget.configuration';
import { TrendLogbookWidgetFilters } from '../../models/TrendLogbookWidgetFilters';

export interface IDashboardLoadService {
    assignValues(data: WidgetValues[], widgets: IWidget[]): void;
    getDashboardInfo(dashboard: Dashboard, referenceGroup: Group): DashboardInfo;
    getRange(period: DashboardPeriod): angular.IPromise<DateRange>;
    getViewModels(widgetConfigurationList: WidgetValuesReadConfiguration[]): angular.IPromise<IValueViewModel[]>;
    load(guid: string): angular.IPromise<ExtendedDashboardInfo>;
    loadInfo(guid: string): angular.IPromise<DashboardInfo>;
    mapDashboard(data: Dashboard): angular.IPromise<DashboardInfo>;
    mapWidgetList(widgetList: IWidget[], seriesDictionary: SeriesDictionary): IWidget[];
    readValues(
        widgetConfigurationList: WidgetValuesReadConfiguration[],
        beginDate: string,
        endDate: string,
        dashboardGuid?: string
    ): angular.IPromise<WidgetValues[]>;
    refresh(
        widgetList: IWidget[],
        beginDate: string,
        endDate: string,
        groupGuid?: string,
        printing?: boolean,
        dashboardGuid?: string,
        force?: boolean
    ): angular.IPromise<WidgetValues[]>;
    setIdentifiers(data: WidgetValues[], viewModels: Dictionary<IValueViewModel>): WidgetValues[];
    buildConfigurations(
        widgetList: IWidget[],
        beginDate: string,
        endDate: string,
        groupGuid?: string
    ): angular.IPromise<{
        alarmsHeatMapConfigurations: AlarmsHeatMapReadConfiguration[];
        alarmsTableConfigurations: AlarmsTableWidgetReadConfiguration[];
        approvalTableConfigurations: { guid: string; groupGuid: string; beginDateTime: string; endDateTime: string }[];
        groupStatesRequest: { widgetGuid: any; beginDate: any; endDate: any; guidList: any }[];
        logbookObjects: LogbookWidgetReadConfiguration[];
        qualityConfigs: QualityWidgetReadConfiguration[];
        spotValuesConfigurations: SpotValuesReadConfiguration[];
        widgetConfiguration: WidgetReadConfiguration[];
    }>;
}

export class DashboardLoadService implements IDashboardLoadService {
    static serviceId = 'dashboardLoadService';

    static $inject = [
        '$q',
        'dashboardService',
        'identifierVmService',
        'dateNavigationService',
        'dateRangeService',
        'seriesService',
        'requestService',
        'tableByGroupService',
        'groupSelectionService',
        'widgetIntervalHelperService',
        'dashboardSeriesService',
        'groupService',
        'frequencyFormatHelperService',
        'mapService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _dashboardService: IDashboardService,
        private _identifierVmService: IIdentifierVmService,
        private _dateNavigationService: IDateNavigationService,
        private _dateRangeService: IDateRangeService,
        private _seriesService: ISeriesService,
        private _requestService: IRequestService,
        private _tableByGroupWidgetService: ITableByGroupService,
        private _groupSelectionService: IGroupSelectionService,
        private _widgetIntervalHelper: IwidgetIntervalHelperService,
        private _dashboardSeriesService: IDashboardSeriesService,
        private _groupService: IGroupService,
        private _frequencyFormatHelperService: IFrequencyFormatHelperService,
        private _mapWidgetService: IMapService
    ) {}

    load(guid: string): angular.IPromise<ExtendedDashboardInfo> {
        let result: any = {};

        return this.loadInfo(guid)
            .then((data) => {
                result = data;
                return this.getRange(data.defaultPeriod);
            })
            .then((data) => {
                if (data) {
                    result.initialRange = data;
                    this._dateRangeService.beginDate(data.beginDateTime);
                    this._dateRangeService.endDate(data.endDateTime);
                }
                const widgetList = result.widgetList;
                const promises: angular.IPromise<WidgetValuesReadConfiguration>[] = widgetList
                    .filter((w) => !!w.getReadConfigurationAsync)
                    .map((f) => f.getReadConfigurationAsync());
                return this._$q.all(promises);
            })
            .then((data: WidgetValuesReadConfiguration[]) => {
                const widgetConfigurationList = data;
                result.widgetConfigurationList = widgetConfigurationList;
                return this.getViewModels(widgetConfigurationList);
            })
            .then((data) => {
                result.viewModels = data;
                return result;
            });
    }

    loadInfo(guid: string): angular.IPromise<DashboardInfo> {
        let dashboard: Dashboard;
        return this._dashboardService
            .getDashboard(guid)
            .then((data: Dashboard) => {
                dashboard = data;
                return this._$q.when(data.referenceGroup ? this._groupService.getByGuid(data.referenceGroup) : null);
            })
            .then((groupDto) => {
                const info = this.getDashboardInfo(dashboard, groupDto.group);
                return this.mapWidgets(info);
            });
    }

    getDashboardInfo(dashboard: Dashboard, referenceGroup: Group): DashboardInfo {
        const result: DashboardInfo = {} as any;
        dashboard.configuration.widgets.forEach((w) => {
            (w as any).data = {};
        });
        result.baseName = dashboard.name;
        result.categories = dashboard.categories;
        result.configuration = dashboard.configuration;
        result.defaultPeriod = dashboard.defaultPeriod;
        result.groupGuids = dashboard.groupGuids;
        result.isDatePickerDisabled = dashboard.isDatePickerDisabled;
        result.referenceGroup = referenceGroup;
        result.types = dashboard.groupTypes;
        result.widgetList = dashboard.configuration.widgets;
        return result;
    }

    mapWidgets(result: DashboardInfo): angular.IPromise<DashboardInfo> {
        return this._dashboardSeriesService.getSeries(result.widgetList).then((seriesDictionary) => {
            result.widgetList = this.mapWidgetList(result.widgetList, seriesDictionary);
            return result;
        });
    }

    mapDashboard(data: Dashboard): angular.IPromise<DashboardInfo> {
        const result: DashboardInfo = {} as any;
        data.configuration.widgets.forEach((w) => {
            (w as any).data = {};
        });
        result.categories = data.categories;
        result.widgetList = data.configuration.widgets;
        result.baseName = data.name;
        result.types = data.groupTypes;
        result.defaultPeriod = data.defaultPeriod;
        result.configuration = data.configuration;
        return this._$q
            .when(data.referenceGroup ? this._groupService.getByGuid(data.referenceGroup) : null)
            .then((groupDto) => {
                result.referenceGroup = groupDto.group;
                return this._dashboardSeriesService.getSeries(result.widgetList);
            })
            .then((seriesDictionary) => {
                result.widgetList = this.mapWidgetList(result.widgetList, seriesDictionary);
                return result;
            });
    }

    mapWidgetList(widgetList: IWidget[], seriesDictionary: SeriesDictionary): IWidget[] {
        return widgetList.map((w) => {
            if (seriesDictionary[w.guid]) {
                w.series = seriesDictionary[w.guid].series;
                w.map = seriesDictionary[w.guid].map;
                if (w.setError) {
                    w.setError(seriesDictionary[w.guid].error);
                }
            }
            return w;
        });
    }

    refresh(
        widgetList: IWidget[],
        beginDate: string,
        endDate: string,
        groupGuid?: string,
        printing?: boolean,
        dashboardGuid?: string,
        force?: boolean
    ): angular.IPromise<WidgetValues[]> {
        beginDate = moment(beginDate).toISOString();
        endDate = moment(endDate).toISOString();

        return this.buildConfigurations(widgetList, beginDate, endDate, groupGuid).then((data) => {
            const {
                alarmsHeatMapConfigurations,
                alarmsTableConfigurations,
                approvalTableConfigurations,
                groupStatesRequest,
                logbookObjects,
                qualityConfigs,
                spotValuesConfigurations,
                widgetConfiguration
            } = data;
            return this.callReadValues(
                beginDate,
                endDate,
                widgetConfiguration,
                groupStatesRequest,
                groupGuid,
                printing,
                dashboardGuid,
                force,
                logbookObjects,
                alarmsTableConfigurations,
                alarmsHeatMapConfigurations,
                approvalTableConfigurations,
                qualityConfigs,
                spotValuesConfigurations
            );
        });
    }

    buildConfigurations(
        widgetList: IWidget[],
        beginDate: string,
        endDate: string,
        groupGuid?: string
    ): angular.IPromise<{
        alarmsHeatMapConfigurations: AlarmsHeatMapReadConfiguration[];
        alarmsTableConfigurations: AlarmsTableWidgetReadConfiguration[];
        approvalTableConfigurations: { guid: string; groupGuid: string; beginDateTime: string; endDateTime: string }[];
        groupStatesRequest: { widgetGuid: any; beginDate: any; endDate: any; guidList: any }[];
        logbookObjects: LogbookWidgetReadConfiguration[];
        qualityConfigs: QualityWidgetReadConfiguration[];
        spotValuesConfigurations: SpotValuesReadConfiguration[];
        widgetConfiguration: WidgetReadConfiguration[];
    }> {
        const widgetConfiguration = this.buildReadConfiguration(widgetList, beginDate, endDate);
        const qualityConfigs = this.buildQualityReadConfigurations(widgetList, beginDate, endDate);
        const groupStatesWidgets = widgetList.filter((t) => t.type === 'GroupStates');
        const tableByGroupWidgets = widgetList.filter((t) => t.type === 'TableByGroup');
        const mapWidgets = widgetList.filter((t) => t.type === 'Map');
        const tbgPromises: angular.IPromise<any>[] = tableByGroupWidgets.map((d: any) =>
            this._tableByGroupWidgetService.getReadConfiguration(d.configuration, groupGuid)
        );
        const mapPromises: angular.IPromise<any>[] = mapWidgets.map((d: any) => this._mapWidgetService.getSeries());
        const promises = tbgPromises.concat(mapPromises);
        const asyncWidgets = tableByGroupWidgets.concat(mapWidgets);

        return this._$q.all(promises).then((data) => {
            asyncWidgets.forEach((asyncWidget, i) => {
                const widgetReadConfiguration = _.find(widgetConfiguration, (w) => w.guid === asyncWidget.guid);
                if (asyncWidget.type === 'TableByGroup') {
                    widgetReadConfiguration.configuration.series = data[i].list;
                    widgetReadConfiguration.configuration.series.forEach((s, j) => (s.id = j));
                    if (widgetReadConfiguration.configuration.extraValues?.length > 0) {
                        widgetReadConfiguration.configuration.extraValues.forEach((ev) => ev.series.forEach((s, j) => (s.id = j)));
                    }
                } else if (asyncWidget.type === 'Map') {
                    widgetReadConfiguration.configuration.series = data[i];
                    widgetReadConfiguration.configuration.series.forEach((s, j) => (s.id = j));
                }
            });
            const groupStatesRequest = groupStatesWidgets.map((d: any, i) => {
                const interval = d.getInterval(beginDate, endDate);
                return {
                    widgetGuid: d.guid,
                    beginDate: interval.beginMoment.toISOString(),
                    endDate: interval.endMoment.toISOString(),
                    guidList: d.series.map((u) => u.guid)
                };
            });

            const index: { [guid: string]: WidgetValuesReadConfiguration } = {};
            widgetConfiguration.forEach((c) => {
                index[c.guid] = c.configuration;
            });

            const logbookObjects = widgetList
                .filter((w) => w.configuration.showLogbook)
                .map((w) => this.seriesToLogbookFilters(w, beginDate, endDate, index, groupGuid));

            const logbookTableObjects = widgetList
                .filter((w) => w.type === 'LogbookTable')
                .map((w) => this.convertLogbookTableConfiguration(w as IWidget & LogbookTableReadConfiguration, groupGuid, beginDate, endDate));
            logbookObjects.push(...logbookTableObjects);

            const alarmsTableConfigurations: AlarmsTableWidgetReadConfiguration[] = widgetList
                .filter((w) => w.type === 'AlarmsTable')
                .map((w) => {
                    const interval = this._widgetIntervalHelper.getInterval(beginDate, endDate, w.configuration as any);
                    return {
                        guid: w.guid,
                        filters: (w.configuration as AlarmsTableConfiguration).filters,
                        beginDateTime: interval.beginMoment.toISOString(),
                        endDateTime: interval.endMoment.toISOString()
                    };
                });

            const alarmsHeatMapConfigurations: AlarmsHeatMapReadConfiguration[] = widgetList
                .filter((w) => w.type === alarmsHeatMapWidgetDefinition.type)
                .map((w) => {
                    const interval = this._widgetIntervalHelper.getInterval(beginDate, endDate, w.configuration as any);
                    const config = angular.copy(w.configuration) as any;
                    config.guid = w.guid;
                    config.beginDateTime = interval.beginMoment.toISOString();
                    config.endDateTime = interval.endMoment.toISOString();
                    return config;
                });

            const approvalTableConfigurations = widgetList
                .filter((w) => w.type === 'approvalTable')
                .filter((w) => w.configuration.groupGuid && w.configuration.groupGuid !== guidEmpty)
                .map((w) => {
                    const { configuration } = w;
                    let groupGuid = configuration.groupGuid;
                    if ((configuration as ApprovalTableConfiguration).isBoundToGroup && w.map) {
                        groupGuid = w.map[groupGuid] ?? groupGuid;
                    }
                    return {
                        guid: w.guid,
                        groupGuid,
                        beginDateTime: beginDate,
                        endDateTime: endDate
                    };
                });

            const spotValuesConfigurations: SpotValuesReadConfiguration[] = widgetList
                .filter((w) => w.type === spotValuesWidgetDefinition.baseType)
                .map((w) => {
                    const cfg = w.configuration as SpotValuesWidgetConfiguration;
                    return {
                        guid: w.guid,
                        beginDateTime: beginDate,
                        endDateTime: endDate,
                        // fall back on current group if not set
                        groupGuid: cfg?.group ?? groupGuid,
                        isBound: cfg?.isBound ?? false,
                        hasLastValue: cfg?.hasLastValue ?? true
                    };
                });
            return {
                alarmsHeatMapConfigurations,
                alarmsTableConfigurations,
                approvalTableConfigurations,
                groupStatesRequest,
                logbookObjects,
                qualityConfigs,
                spotValuesConfigurations,
                widgetConfiguration
            };
        });
    }

    readValues(
        widgetConfigurationList: WidgetValuesReadConfiguration[],
        beginDate: string,
        endDate: string,
        dashboardGuid?: string
    ): angular.IPromise<WidgetValues[]> {
        const widgetList = widgetConfigurationList.map((w) => {
            return {
                guid: w.guid,
                configuration: w
            };
        });
        return this.callReadValues(beginDate, endDate, widgetList, [], undefined, false, dashboardGuid);
    }

    assignValues(data: WidgetValues[], widgets: IWidget[]): void {
        // reassign all data to each widget
        data.forEach((d) => {
            const widget = _.find(widgets, (w) => w.guid === d.guid);
            // general case
            if (widget && widget.guid !== guidEmpty) {
                (widget as any).data = d;

                const errorValues = _.values(d.errors);
                widget.error = errorValues?.length > 0 ? new WideError(ErrorLevel.Error, _.uniq(_.values(d.errors)).join('\r\n')) : undefined;
            }
            // special case for variables
            else if (d.type === 'TextVariables') {
                widgets.filter((w) => w.type === 'text').forEach((w) => ((w as any).data = d.variables));
            }
            // special case for executed query
            else if (d.type === 'ExecutedQueries') {
                const guids = Object.keys(d.queries);
                widgets
                    .filter((w) => w.getSeriesGuids && _.intersection(w.getSeriesGuids(), guids).length > 0)
                    .forEach((w) => {
                        w.executedQueries = d.queries;
                        w.executedQueriesMap = d.map[w.guid];
                    });
            }
        });
    }

    private buildReadConfiguration(widgetList: IWidget[], beginDate: string, endDate: string): WidgetReadConfiguration[] {
        const widgetConfiguration = this.getWidgetReadConfigurations(widgetList, beginDate, endDate);

        const widgestDic = {};
        widgetList.forEach((w) => (widgestDic[w.guid] = w));
        widgetConfiguration.forEach((w) => {
            const widget = widgestDic[w.guid];
            if (widget && widget.map) {
                this.updateGuids(w.configuration.series, widget.map);
                if (w.configuration.extraValues) {
                    w.configuration.extraValues.forEach((e) => this.updateGuids(e.series, widget.map));
                }
                if (w.configuration.aggregationReadConfiguration) {
                    const { aggregationReadConfiguration } = w.configuration;
                    aggregationReadConfiguration.guid = widget.map[aggregationReadConfiguration.guid] ?? aggregationReadConfiguration.guid;
                    aggregationReadConfiguration.series = aggregationReadConfiguration.series.map((s) =>
                        Object.assign({}, s, { guid: widget.map[s.guid] ?? s.guid })
                    );
                }
            }
        });
        return widgetConfiguration;
    }

    private updateGuids(series: IReadSeriesConfiguration[], map: { [guid: string]: any }) {
        series
            .filter((s) => s.isBoundToGroup)
            .forEach((s) => {
                const guid = map[s.guid];
                if (guid) {
                    s.guid = guid;
                }
            });
    }

    private buildQualityReadConfigurations(widgetList: any[], _begin: string, _end: string): QualityWidgetReadConfiguration[] {
        return widgetList
            .filter((d) => !!d.configuration && d.configuration.qualityWidgetType !== undefined)
            .map((d) => {
                return {
                    guid: d.guid,
                    type: d.configuration.qualityWidgetType,
                    max: d.configuration.max || 10
                };
            });
    }

    private seriesToLogbookFilters(
        w: IWidget,
        beginDate: string,
        endDate: string,
        configurations: WidgetValuesReadConfigurationDictionary,
        groupGuid: string
    ): LogbookWidgetReadConfiguration {
        let series: string[];
        if (configurations[w.guid]) {
            series = configurations[w.guid].series.map((s) => s.guid);
        } else if (w.getSeriesGuids) {
            series = w.getSeriesGuids();
        } else {
            series = [];
        }
        const dates = this._widgetIntervalHelper.getInterval(beginDate, endDate, w.configuration as any);
        const lbConfig = w.configuration as TrendLogbookWidgetFilters;
        const config = {
            beginDate: dates.beginMoment.toISOString(),
            endDate: dates.endMoment.toISOString(),
            guid: w.guid,
            filters: {
                seriesGuids: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? [] : series,
                includeChildren: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.includeChildren : false,
                groupGuids: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.groupGuids : [],
                categories: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.categories : [],
                groupTypes: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.groupTypes : [],
                severities: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.severities : [],
                sortBy: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.sortBy : LogbookEntrySortOrder.Date,
                dateOrder: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.dateOrder : DateOrder.Increasing,
                isBoundToGroup: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.isBoundToGroup : false,
                isAlwaysShow: lbConfig.logbookEntriesShownType === TrendLogbookEntriesType.Custom ? lbConfig.isAlwaysShow : false
            },
            series: [] as any[],
            readMode: LogbookTableReadMode.ByTimeStamp,
            isPreviousIncluded: false,
            maxItems: undefined,
            logbookEntriesShownType: lbConfig.logbookEntriesShownType
        };
        if (config.filters.isBoundToGroup) {
            config.filters.groupGuids = groupGuid ? [groupGuid] : [];
            config.filters.groupTypes = [];
        }
        return config;
    }

    private convertLogbookTableConfiguration(
        d: IWidget & LogbookTableReadConfiguration,
        groupGuid: string,
        beginDate: string,
        endDate: string
    ): LogbookWidgetReadConfiguration {
        const configuration = d.configuration;
        const logbookWidgetFilters = Object.assign({}, configuration.filters);
        if (logbookWidgetFilters.isBoundToGroup) {
            logbookWidgetFilters.groupGuids = groupGuid ? [groupGuid] : [];
            logbookWidgetFilters.groupTypes = [];
        }
        const dates = this._widgetIntervalHelper.getInterval(beginDate, endDate, configuration);
        return {
            beginDate: dates.beginMoment.toISOString(),
            endDate: dates.endMoment.toISOString(),
            guid: d.guid,
            filters: logbookWidgetFilters,
            series:
                d.series
                    ?.map((s) => s.configuration)
                    .map((s) => {
                        return {
                            guid: s.guid,
                            valueType: s.value,
                            aggregationMode: s.aggregationMode,
                            isBoundToGroup: s.isBoundToGroup,
                            hasAlarm: s.hasAlarm
                        };
                    }) ?? [],
            readMode: configuration.readMode === null || configuration.readMode === undefined ? LogbookTableReadMode.ByTimeStamp : configuration.readMode,
            isPreviousIncluded: configuration.hasPreviousIncluded,
            maxItems: configuration.maxItems,
            maxTotalItems: configuration.maxTotalItems,
            logbookEntriesShownType: configuration.logbookEntriesShownType
        };
    }

    private callReadValues(
        beginDate: string,
        endDate: string,
        widgetConfigurationList: WidgetReadConfiguration[],
        groupStatesRequestObjects: GroupStatesRequest[],
        groupGuid?: string,
        printing?: boolean,
        dashboardGuid?: string,
        force: boolean = false,
        logbookWidgetsObjects: LogbookWidgetReadConfiguration[] = [],
        alarmsTableConfigurations: AlarmsTableWidgetReadConfiguration[] = [],
        alarmsHeatMapConfigurations: AlarmsHeatMapReadConfiguration[] = [],
        approvalTableReadConfigurations: ApprovalTableReadConfiguration[] = [],
        qualityConfigs: QualityWidgetReadConfiguration[] = [],
        spotValuesConfigurations: SpotValuesReadConfiguration[] = []
    ): angular.IPromise<WidgetValues[]> {
        const selectedGroup = this._groupSelectionService.selectedGroup();
        const currentGroup = selectedGroup ? selectedGroup.guid : undefined;
        const readConfiguration: DashboardValuesReadConfiguration = {
            alarmsHeatMapConfigurations: alarmsHeatMapConfigurations,
            alarmsTableConfigurations,
            approvalTableConfigurations: approvalTableReadConfigurations,
            beginDate: beginDate,
            currentDashboard: dashboardGuid,
            currentGroup: groupGuid ? groupGuid : currentGroup,
            endDate: endDate,
            groupStatesRequestObjects: groupStatesRequestObjects,
            logbookWidgetsObjects,
            printing: printing,
            qualityWidgetConfigurations: qualityConfigs,
            spotValuesConfigurations: spotValuesConfigurations,
            widgetConfigurationList: widgetConfigurationList
        };

        readConfiguration.widgetConfigurationList
            .filter((d) => {
                return d.configuration.series && d.configuration.series.filter((s) => s.offset && s.offset.interval).length;
            })
            .map((d) => d.configuration)
            .forEach((d) =>
                d.series
                    .filter((s) => s.offset && s.offset.interval)
                    .forEach((s) => {
                        s.range = {
                            beginDate: moment(d.beginDate).add(this._frequencyFormatHelperService.toDuration(s.offset)).toISOString(),
                            endDate: moment(d.endDate).add(this._frequencyFormatHelperService.toDuration(s.offset)).toISOString()
                        };
                    })
            );

        return this.readById(readConfiguration, force);
    }

    private readById(readConfiguration: DashboardValuesReadConfiguration, force?: boolean): angular.IPromise<WidgetValues[]> {
        if (force) {
            this._requestService.cleanCachedPostValue(dashboardValuesUrl, readConfiguration);
        }
        return this._requestService
            .cachedPostAtV1<DashboardReadResultDto>(dashboardValuesUrl, readConfiguration)
            .then((data) => {
                // reconstruct values based on array
                this.reconstructBasedOnArrray(data);
                return data;
            })
            .then((data) => buildWidgetValues(data, readConfiguration));
    }

    private reconstructBasedOnArrray(data: DashboardReadResultDto) {
        _.forEach(data.widgetResults, (r) => {
            _.forEach(r.seriesValues, (sv) => {
                const len = sv.arrayOfValuesFields?.length ?? 0;
                if (len > 0) {
                    sv.values = sv.arrayOfValues.map((item) => {
                        const ret = {} as DateValues;
                        for (let i = 0; i < len; i++) {
                            const field = _.camelCase(sv.arrayOfValuesFields[i]);
                            ret[field] = item[i];
                        }
                        return ret;
                    });
                }
                delete sv.arrayOfValuesFields;
                delete sv.arrayOfValues;
            });
        });
    }

    getWidgetReadConfigurations(widgetList: IWidget[], beginDate: string, endDate: string): WidgetReadConfiguration[] {
        return widgetList
            .filter((d) => !!d.getReadConfiguration)
            .map((d) => {
                const configuration = {
                    guid: d.guid,
                    configuration: d.getReadConfiguration(beginDate, endDate)
                };
                return configuration;
            });
    }

    getViewModels(widgetConfigurationList: WidgetValuesReadConfiguration[]): angular.IPromise<IValueViewModel[]> {
        const series = _.flatten(widgetConfigurationList.map((d) => d.series));
        return this._seriesService.getUpdatedGuids(series as any).then((data) => {
            const guids = _.chain(data)
                .map((d) => d.guid)
                .uniq()
                .value();
            //Warm cache
            //return this._identifierVmService.getViewModelsFromApiFromGuidList(guids);
            return this._identifierVmService.getSomeViewModels(guids);
        });
    }

    getRange(period: DashboardPeriod): angular.IPromise<DateRange> {
        let promise: angular.IPromise<DateRange>;
        switch (period) {
            case DashboardPeriod.None:
                promise = this._dateNavigationService.getLastRange();
                break;
            case DashboardPeriod.Today:
                promise = this._dateNavigationService.getToday();
                break;
            case DashboardPeriod.Yesterday:
                promise = this._dateNavigationService.getYesterday();
                break;
            case DashboardPeriod.Last24H:
                promise = this._dateNavigationService.getLast24h();
                break;
            case DashboardPeriod.Last7D:
                promise = this._dateNavigationService.getLast7Days();
                break;
            case DashboardPeriod.ThisWeek:
                promise = this._dateNavigationService.getThisWeek();
                break;
            case DashboardPeriod.LastWeek:
                promise = this._dateNavigationService.getLastWeek();
                break;
            case DashboardPeriod.Last30D:
                promise = this._dateNavigationService.getLast30Days();
                break;
            case DashboardPeriod.ThisMonth:
                promise = this._dateNavigationService.getThisMonth();
                break;
            case DashboardPeriod.LastMonth:
                promise = this._dateNavigationService.getLastMonth();
                break;
            case DashboardPeriod.WeekToDate:
                promise = this._dateNavigationService.getWeekToDate();
                break;
            case DashboardPeriod.MonthToDate:
                promise = this._dateNavigationService.getMonthToDate();
                break;
            case DashboardPeriod.NextMonth:
                promise = this._dateNavigationService.getNextMonth();
                break;
            case DashboardPeriod.LastQuarter:
                promise = this._dateNavigationService.getLastQuarter();
                break;
            case DashboardPeriod.Last3Months:
                promise = this._dateNavigationService.getLast3Months();
                break;
            case DashboardPeriod.ThisQuarter:
                promise = this._dateNavigationService.getThisQuarter();
                break;
            case DashboardPeriod.QuarterToDate:
                promise = this._dateNavigationService.getQuarterToDate();
                break;
            case DashboardPeriod.NextQuarter:
                promise = this._dateNavigationService.getNextQuarter();
                break;
            case DashboardPeriod.LastYear:
                promise = this._dateNavigationService.getLastYear();
                break;
            case DashboardPeriod.Last12Months:
                promise = this._dateNavigationService.getLast12Months();
                break;
            case DashboardPeriod.ThisYear:
                promise = this._dateNavigationService.getThisYear();
                break;
            case DashboardPeriod.YearToDate:
                promise = this._dateNavigationService.getYearToDate();
                break;
            case DashboardPeriod.NextYear:
                promise = this._dateNavigationService.getNextYear();
                break;
            default:
                break;
        }
        return promise;
    }

    setIdentifiers(data: WidgetValues[], viewModels: Dictionary<IValueViewModel>): WidgetValues[] {
        if (viewModels) {
            data.filter((d) => d.results && !!d.results.forEach).forEach((d) => {
                d.results
                    .filter((r) => !!r.identifierGuid && !!viewModels[r.identifierGuid])
                    .forEach((r) => {
                        r.identifier = viewModels[r.identifierGuid];
                        this.setTag(r, viewModels);
                    });
            });
        }
        return data;
    }

    private setTag(r: any, viewModels: Dictionary<IValueViewModel>) {
        if (r.tagDefinitionGuid) {
            r.identifier.tagDefinitionGuid = r.tagDefinitionGuid;
        }
        if (r.identifier.tagDefinitionGuid !== guidEmpty) {
            const foundTag = viewModels[r.tagDefinitionGuid];
            if (foundTag) {
                r.identifier.tag = foundTag;
            }
        }
    }
}
